// Enhanced structured logging utility
// Environment-aware with support for error tracking integration
/* eslint-disable no-console */

import { captureError } from './error-tracking';

interface LogContext {
  [key: string]: unknown;
}

const getRuntimeEnv = (): Record<string, string | boolean | undefined> => {
  if (typeof import.meta !== 'undefined' && import.meta?.env) {
    return import.meta.env as Record<string, string | boolean | undefined>;
  }
  if (typeof process !== 'undefined' && process?.env) {
    return process.env as Record<string, string | undefined>;
  }
  return {};
};

const runtimeEnv = getRuntimeEnv();
const isDev = runtimeEnv.DEV ?? runtimeEnv.NODE_ENV !== 'production';
const isProd = runtimeEnv.PROD ?? runtimeEnv.NODE_ENV === 'production';
const debugEnabled = runtimeEnv.VITE_DEBUG === 'true' || runtimeEnv.DEBUG === 'true';

class Logger {
  private formatMessage(level: string, message: string, context?: LogContext): void {
    const timestamp = new Date().toISOString();
    const logData = {
      timestamp,
      level,
      message,
      ...(context && { context }),
    };

    // In development, pretty print
    if (isDev) {
      const prefix = `[${timestamp}] ${level.toUpperCase()}:`;
      if (context) {
        console.group(prefix, message);
        console.log(context);
        console.groupEnd();
      } else {
        console.log(prefix, message);
      }
    }

    // In production, errors should still be logged for error tracking
    if (isProd && level === 'ERROR') {
      console.error(message, context);
      // Send to error tracking service (non-blocking)
      try {
        captureError(new Error(message), {
          component: 'Logger',
          additionalData: context,
        });
      } catch (error) {
        // Silently fail if error tracking is not available
      }
    }
  }

  error(message: string, context?: LogContext): void {
    this.formatMessage('ERROR', message, context);
  }

  warn(message: string, context?: LogContext): void {
    // Always log warnings (they're important)
    this.formatMessage('WARN', message, context);
  }

  info(message: string, context?: LogContext): void {
    // Only log info in development or when debug is enabled
    if (isDev || debugEnabled) {
      this.formatMessage('INFO', message, context);
    }
  }

  debug(message: string, context?: LogContext): void {
    // Only log debug when explicitly enabled
    if (isDev && debugEnabled) {
      this.formatMessage('DEBUG', message, context);
    }
  }

  // Legacy support for spread args (will be phased out)
  log(...args: unknown[]): void {
    if (isDev) {
      console.log(...args);
    }
  }
}

export const logger = new Logger();
